import { useGoogleLogin } from "@react-oauth/google";
import { baseUrl } from "./DataServices/Configuration";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Loading from "./Loading";
import Notification from "./Notification";

interface LoginProps {
  setToken: (token: string) => void;
}

const Login: React.FC<LoginProps> = ({ setToken }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [loginMethod, setLoginMethod] = useState<"google" | "username">(
    "google",
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // Check if user is already logged in
  useEffect(() => {
    const checkExistingToken = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        // If token exists, redirect to home (App.tsx will validate it)
        navigate("/home", { replace: true });
      }
    };
    checkExistingToken();
  }, [navigate]);

  const handleUsernameLogin = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userName: username, password }),
      });

      if (!response.ok) {
        throw new Error("Login failed");
      }

      const data = await response.json();
      localStorage.setItem("token", data.access_token);
      localStorage.setItem(
        "expires_at",
        (Date.now() + data.expires_in * 1000).toString(),
      );
      setToken(data.access_token);
      setLoading(false);
      navigate("/home");
    } catch (error) {
      setLoading(false);
      setOpenNotification(true);
      console.error("Login Error:", error);
    }
  };

  const login = useGoogleLogin({
    onSuccess: (response) => {
      // Send the authorization code to your backend server
      const authorizationCode = response.code;
      const url = baseUrl + "/api/auth/google-login";
      setLoading(true);
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ authorizationCode }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Server response was not ok");
          }
          return res.json();
        })
        .then((data) => {
          localStorage.setItem("token", data.access_token);
          localStorage.setItem(
            "expires_at",
            (Date.now() + data.expires_in * 1000).toString(),
          );
          setToken(data.access_token);
          setLoading(false);
          navigate("/home");
        })
        .catch((err) => {
          setLoading(false);
          setOpenNotification(true);
          console.error("Login Error:", err); // Log the exact error
        });
    },
    flow: "auth-code", // Ensure you're using the "auth-code" flow for backend communication
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100">
      <div className="text-center bg-white p-8 rounded-md border shadow-xl max-w-md w-full">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Login</h2>

        <div className="mb-4">
          <button
            onClick={() => setLoginMethod("google")}
            className={`mr-2 px-4 py-2 rounded ${loginMethod === "google" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
          >
            Google Login
          </button>
          <button
            onClick={() => setLoginMethod("username")}
            className={`px-4 py-2 rounded ${loginMethod === "username" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
          >
            Username Login
          </button>
        </div>

        {loginMethod === "google" && (
          <>
            <p className="text-gray-600 mb-6">
              Please login with your Google account to continue.
            </p>
            {loading ? (
              <div className="flex w-full items-center justify-center">
                <Loading />
              </div>
            ) : (
              <button
                onClick={() => login()}
                className="flex items-center mx-auto justify-center bg-white border border-gray-300 rounded-md py-2 px-4 text-gray-700 shadow-sm hover:bg-gray-100 transition-all duration-200"
              >
                <img
                  src={googleLogoUrl}
                  alt="Google Logo"
                  className="w-6 h-6 mr-3"
                />
                <span className="font-medium">Sign in with Google</span>
              </button>
            )}
          </>
        )}

        {loginMethod === "username" && (
          <>
            <p className="text-gray-600 mb-6">
              Please enter your username and password to continue.
            </p>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-4">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {loading ? (
              <div className="flex w-full items-center justify-center">
                <Loading />
              </div>
            ) : (
              <button
                onClick={handleUsernameLogin}
                className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition-all duration-200"
              >
                Login
              </button>
            )}
          </>
        )}

        <p className="text-gray-500 mt-4">
          By signing in, you agree to our{" "}
          <a href="#" className="text-blue-600">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="#" className="text-blue-600">
            Privacy Policy
          </a>
          .
        </p>
      </div>
      <Notification
        isOpen={openNotification}
        setIsOpen={setOpenNotification}
        message={"Login failed"}
        messageType={"error"}
      />
    </div>
  );
};

export default Login;

const googleLogoUrl =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/150px-Google_%22G%22_logo.svg.png";
